import { useMutation, useQuery } from '@apollo/client/react';
import {
  GET_BANK_ACCOUNTS,
  CREATE_LINKED_ACCOUNTS,
  UPDATE_BANK_DETAILS,
  SET_IS_PRIMARY_ACCOUNT,
  VERIFY_LINKED_ACCOUNT,
} from 'api/graphql/account';
import { useVendor } from 'features/vendor';

/** Lists the vendor's linked bank accounts. */
export function useBankAccounts() {
  const { data, loading, error, refetch } = useQuery(GET_BANK_ACCOUNTS, {
    notifyOnNetworkStatusChange: true,
  });
  return { accounts: data?.getBankAccounts ?? [], loading, error, refetch };
}

/**
 * Create / update / set-primary / verify bank accounts.
 * NOTE: create & update return `{ error, message }` (success = no `error`).
 */
export function useBankAccountMutations() {
  const { vendorId } = useVendor();
  const refetchQueries = [{ query: GET_BANK_ACCOUNTS }];

  const [create, createState] = useMutation(CREATE_LINKED_ACCOUNTS, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [update, updateState] = useMutation(UPDATE_BANK_DETAILS, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [setPrimary, setPrimaryState] = useMutation(SET_IS_PRIMARY_ACCOUNT, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [verify, verifyState] = useMutation(VERIFY_LINKED_ACCOUNT, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  return {
    // `targetVendorId` is the SHOP to attach the account to (defaults to active).
    createAccount: async (input, targetVendorId) =>
      (await create({ variables: { vendorId: targetVendorId || vendorId, input } })).data
        ?.createLinkedAccounts ?? null,
    updateAccount: async (accountId, input) =>
      (await update({ variables: { accountId, input } })).data?.updateBankDetails ?? null,
    setPrimaryAccount: async (accountId) =>
      (await setPrimary({ variables: { accountId } })).data?.setIsPrimaryAccount ?? null,
    verifyAccount: async (razorpayaccountId) =>
      (await verify({ variables: { razorpayaccountId } })).data?.verifyLinkedAccount ?? null,
    creating: createState.loading,
    updating: updateState.loading,
    settingPrimary: setPrimaryState.loading,
    verifying: verifyState.loading,
  };
}
